/**
 * Constant-time legend width measurer using canvas measureText.
 * Delegates to each plugin's legendFactory.computeWidth() — no
 * type dispatch, no DOM access.
 *
 * Font strings are computed from design system CSS variables
 * (--cap-height, --baseline, --small-size, --h6-size) rather than
 * probed from rendered DOM elements.
 */

import type { Option } from "@tento-lona/sheets";
import type { TextMeasurer } from "./default-legend-layout.ts";
import type { SheetRow } from "@tento-lona";
import type { RenderPluginManager } from "./render-plugin.ts";
import { SheetLegend } from "./sheet-legend.ts";
import { Tracing } from "@tento-core/tracing";

/** Nested rows are indented 20px per depth level (SheetLegend.Nested marginLeft). */
const DEPTH_INDENT = 20;

export class PretextLegendMeasurer {
  readonly #textMeasurer: TextMeasurer;

  private constructor(defaultFont: string, fonts: Record<string, string>) {
    const canvas = new OffscreenCanvas(1, 1);
    const ctx = canvas.getContext("2d")!;
    ctx.font = defaultFont;
    const cachedDefault = defaultFont;

    this.#textMeasurer = {
      measureText: (text: string, font: Option<string> = null): number => {
        if (font && font !== cachedDefault) ctx.font = font;
        const w = ctx.measureText(text).width;
        if (font && font !== cachedDefault) ctx.font = cachedDefault;
        return w;
      },
      fonts,
    };
  }

  /**
   * Create a measurer from the design system's CSS variables.
   * Reads --cap-height, --baseline, --small-size, --h6-size from
   * the element's computed style to derive canvas font strings.
   * No sentinel or shadow DOM traversal needed.
   */
  static fromStyleContext($el: HTMLElement): PretextLegendMeasurer {
    const style = getComputedStyle($el);
    const capHeight = parseFloat(style.getPropertyValue("--cap-height")) || 0.125;
    const baseline = parseFloat(style.getPropertyValue("--baseline")) || 0.155;
    const fontHeight = 1 - capHeight - baseline;
    const fontFamily = style.fontFamily || "Helvetica, sans-serif";

    // CSS vars are in rem (e.g., "0.5625rem"). Resolve to px via root font-size.
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const resolveRem = (varName: string, fallbackRem: number): number => {
      const raw = style.getPropertyValue(varName).trim();
      const rem = raw ? parseFloat(raw) : fallbackRem;
      return rem * rootFontSize;
    };

    const smallCapheightPx = resolveRem("--small-size", 0.5625); // 9px
    const h6CapheightPx = resolveRem("--h6-size", 0.75); // 12px

    const smallFontSize = smallCapheightPx / fontHeight;
    const h6FontSize = h6CapheightPx / fontHeight;

    const smallFont = `${smallFontSize}px ${fontFamily}`;
    const h6Font = `${h6FontSize}px ${fontFamily}`;

    return new PretextLegendMeasurer(smallFont, {
      label: smallFont,
      tzname: smallFont,
      title: h6Font,
    });
  }

  /**
   * Create a measurer from explicit font strings (legacy, for tests).
   */
  static fromFont(defaultFont: string, fonts: Record<string, string> = {}): PretextLegendMeasurer {
    return new PretextLegendMeasurer(defaultFont, fonts);
  }

  /**
   * Compute the legend column width for a set of top-level rows.
   * Walks the tree recursively to account for depth-based indentation
   * (20px margin-left per depth level). Returns floored + 1, min 150.
   *
   * @param hasLayout — predicate to check if a row has a layout entry.
   *   Rows without layout are not rendered and should be skipped.
   */
  measure(
    rows: Iterable<SheetRow.Joined.Handle>,
    sheetTimescale: string,
    pluginManager: RenderPluginManager,
    hasLayout?: (row: SheetRow.Joined.Handle) => boolean,
  ): number {
    let max = 0;
    const walk = (items: Iterable<SheetRow.Joined.Handle>, depth: number) => {
      for (const row of items) {
        if (hasLayout && !hasLayout(row)) continue;
        if (row.hideLegend) continue;
        const plugin = pluginManager.get(row).plugin();
        const factory =
          plugin.legendFactory ??
          SheetLegend.DefaultFactory;
        const indent = depth > 0 ? depth * DEPTH_INDENT : 0;
        const width = factory.computeWidth(row, this.#textMeasurer, sheetTimescale) + indent;
        if (width > max) max = width;
        walk(row.children(), depth + 1);
      }
    };
    walk(rows, 0);
    return Math.max(150, Math.floor(max + 1));
  }

  measureWithDebug(
    rows: Iterable<SheetRow.Joined.Handle>,
    sheetTimescale: string,
    pluginManager: RenderPluginManager,
    hasLayout?: (row: SheetRow.Joined.Handle) => boolean,
  ): { width: number; widestRow: Option<string> } {
    let max = 0;
    let widestLabel: Option<string> = null;
    const perRow: { type: string; label: string; depth: number; raw: number; indent: number; total: number; hasFactory: boolean; hidden: boolean }[] = [];
    const walk = (items: Iterable<SheetRow.Joined.Handle>, depth: number) => {
      for (const row of items) {
        // Skip rows without layout — they're not rendered in the legend.
        if (hasLayout && !hasLayout(row)) continue;
        const plugin = pluginManager.get(row).plugin();
        const factory =
          plugin.legendFactory ??
          SheetLegend.DefaultFactory;
        const indent = depth > 0 ? depth * DEPTH_INDENT : 0;
        const raw = factory.computeWidth(row, this.#textMeasurer, sheetTimescale);
        const width = raw + indent;
        const hidden = row.hideLegend ?? false;
        // Skip rows that don't render a legend — the sentinel doesn't
        // measure them either (Nested.bindNow returns early for hideLegend).
        // Also skip subsumed rows (e.g., calendar-list subsumed by calendar).
        if (hidden) continue;
        perRow.push({
          type: row.descriptor.id,
          label: (row.label ?? "?").slice(0, 20),
          depth,
          raw,
          indent,
          total: width,
          hasFactory: plugin?.legendFactory != null,
          hidden,
        });
        if (width > max) {
          max = width;
          widestLabel = `${row.descriptor.id}:${row.label ?? "?"}(${Math.round(width)}px)`;
        }
        walk(row.children(), depth + 1);
      }
    };
    walk(rows, 0);
    const result = Math.max(150, Math.floor(max + 1));
    Tracing.log("legend-pretext/measure", { perRow, max, result });
    return {
      width: result,
      widestRow: widestLabel,
    };
  }
}
